let next_id = 0;

function monthlyFrequencyConversionFactor(frequency){
    switch(frequency){
        case 'Yearly':      return 0.08333;
        case 'Quarterly':   return 0.33333;
        case 'Monthly':     return 1;
        case 'Fortnightly': return 2.167;
        case 'Weekly':      return 4.333;
        default:            return 1;
    }
}

function checkBussinessQueryType(query) {
    const acnPattern = /^\s*\d{3}\s*\d{3}\s*\d{3}\s*$/;
    const abnPattern = /^\s*\d{2}\s*\d{3}\s*\d{3}\s*\d{3}\s*$/;

    if (acnPattern.test(query)) {
        return 'acn';
    } else if (abnPattern.test(query)) {
        return 'abn';
    } else {
        return 'name';
    }
}


function frequencyConversionFactor(from, to){
    let from_factor = monthlyFrequencyConversionFactor(from);
    let to_factor = monthlyFrequencyConversionFactor(to);
    return from_factor / to_factor;
}

function calculateRepayments(amount, anual_rate, term, term_unit, repayment_frequency, monthly_account_fee, balloon_amount, repayment_type = 'Arrears'){
    amount =                Number.parseFloat(amount) || 0;
    anual_rate =            Number.parseFloat(anual_rate) || 0;
    term =                  Number.parseFloat(term) || 0;
    monthly_account_fee =   Number.parseFloat(monthly_account_fee) || 0;
    balloon_amount =   Number.parseFloat(balloon_amount) || 0;

    let payments_per_annum = frequencyConversionFactor(repayment_frequency, 'Yearly');
    let number_of_payments = Math.round(term / frequencyConversionFactor(term_unit, repayment_frequency));
    let rate = (anual_rate / payments_per_annum) / 100;
    let charge_per_payment = monthly_account_fee * 12 / payments_per_annum;

    let repayment_amount = amount;

    if (repayment_type === 'Advance') {
      repayment_amount -= amount * rate;
    }
    let payments = (repayment_amount - (balloon_amount / ((1 + rate) ** number_of_payments))) * rate / (1 - (1 + rate) ** (-number_of_payments)) + charge_per_payment;

    return payments || null;
}

export default {
    id(object){
        if(object.hasOwnProperty("__objectID__")) {
            return object.__objectID__;
        } else {
            let id = next_id++;
            Object.defineProperty(object, "__objectID__", {
                "configurable" : false,
                "enumerable" :   false,
                "get" :          () => id,
                "set" :          () => null,
            });
            return object.__objectID__;
        }
    },
    arrayRemove(array, item){
        let index = array.indexOf(item);
        if(index >= 0){
          array.splice(index, 1);
          return true;
        }else{
            return false;
        }
    },

    /**
     * Manages and performs live typed queries but limites the outgoing requests to the api endpoints limits.
     * Once initialised, one can request queries using the query() method, 
     * this will queue and then run the query once the currently running query returns.
     * Once the last query request returns only the last requested query will be run, all inbetween ones are discarded.
     * 
     * Example timeline:
     * time     input event     network events
     * 1        'a'             send request with query 'a'
     * 2        'ab'        
     * 3        'abc'       
     * 4                        query 'a' returns, send request with query 'abc'
     * 5        'abcd'      
     * 6        'abcde'      
     * 7                        query 'abc' returns, send request with query 'abcde'
     * 8
     * 9                        query 'abcde' returns
     * 
     * dispite 5 input change events, only 3 network requests were made.
     * 
     * Note: this service does not implement the actual requests calls, rather it calles a callback function at appropriate times
     */
    LiveQueryer: class {
        
        /**
         * init service
         * @param {Function} apiFunction callback function triggered everytime a query is to be sent.
         */
        constructor(apiFunction) {
            this.apiFunction = apiFunction;
            this.current = null;
            this.next = null;

            this.latest_run_id = 0;
            this.last_returned_id = 0;
        }

        /**
         * requests a new query, this may result in an immediate or delayed call to this.apiFunction()
         * @param {String} query the latest query parameter to be used on the next this.apiFunction() call
         */
        query(query, force=false) {

            if(query == this.current){
                return;
            }

            this.next = query;

            // if no current queries in progress, run this query immediately
            if(this.current === null || force){
                this.runNext();
            }
        }

        /**
         * Checks if a request id is the most recent sent out request that has already returned.
         * That is if out of all returned requests, is this id the latest.
         * This is used to prevent older requests that return after new requests to override newer
         * results with older results because the older requests happened to return later.
         * NOTE
         * @param {Number} id to be checked
         * @returns {Boolean} true if there is returned requests that was sent out after this id
         */
        isLatestRequest(id) {
            return id >= this.last_returned_id;
        }

        /**
         * Returns if the most recently sent out request has returned.
         * When this returns true, the search is considered complete,
         * that is the search is no longer in progress.
         * NOTE: returning true does NOT mean ALL requests have returned,
         * it means that there will not be any newer requests returning.
         * (until the user begins typing again)
         * @returns {Boolean} true if last sent request has returned
         */
        hasLastRequestReturned() {
            return this.last_returned_id == this.latest_run_id;
        }

        /**
         * perform a query request by calling the this.apiFunction() callback
         */
        async runNext() {
            let id = ++this.latest_run_id;

            // indicate query in progress
            this.current = this.next;
            this.next = null;

            // perform request by calling this.apiFunction() and awaiting completion
            try{
                await this.apiFunction(this.current, id);
            }catch(error){
                console.error('Exception encountered during LiveQueryer.runNext() apiFunction() call');
                console.error(error);
            }

            // indicate no query in progress
            this.current = null;
            if(this.isLatestRequest(id)){
                this.last_returned_id = id;
            }

            // if a query is in queue, run it
            if(this.next !== null){
                this.runNext();
            }
        }
    },

    /**
     * This service is to coordinate multiple array and non array editors to 
     * always ensure an extra blank entry is generated for as long as total sum
     * of durations in months does not exceed the target.
     * 
     * To use this you instantiate and register AutoDurationMandationService.Source class 
     * for every source and dedicate one source as the generative source, whenever this
     * service decisdes create or remove an additional entry, this generative source's
     * relevant call backs are made which are handled by the editor components to create
     * or remove the relevant entries in their data.
     */
    AutoDurationMandationService: class {

        /**
         * @param {int} target_months number of months that are desired, if the total is less than this, the services will ensure an extra 
         */
        constructor(target_months) {

            let auto_duration_mandation_service = this;

            /**
             * Represents one source, a source may be a single entry or an array of entries which get aggregated 
             * by the aggregation_method into a single number of months.
             * It holds methods to extract the number of months from the entries and to notify of source changes
             */
            this.Source = class {

                /**
                 * @param {Object|Array} data the actual data from which duration data is extracted
                 * @param {Function} durationGetter a function to extract a duration object from data or a data entry
                 * @param {String} aggregation_method if data is an array, determines how to combine the entries durations. options: 'none', 'sum', 'max'
                 */
                constructor(data, durationGetter, aggregation_method='none', append_generated_entry=false) {
                    this.data = data;
                    this.durationGetter = durationGetter;
                    this.aggregation_method = aggregation_method;
                    this.append_generated_entry = append_generated_entry;

                    // reference to parent AutoDurationMandationService instance
                    this.auto_duration_mandation_service = auto_duration_mandation_service;
                }

                /**
                 * Converst a duration objetc to number of months. 
                 * This function can be replaced if a source uses a different format
                 * @param {Object} duration an object in the form of { years:Number, months:Number }
                 * @returns {Number} number of months a single entry represents
                 */
                durationToMonths(duration) {
                    
                    let years = Number.parseInt(duration.years) || 0;
                    let months = Number.parseInt(duration.months) || 0;

                    if(years < 0) years = 0;
                    if(months < 0) months = 0;
                    if(months > 11) months = 11;

                    return 12 * years + months;
                }

                /**
                 * computes the total number of months this source holds, 
                 * this.aggregation_method is used to determine how to combine array entries into a single number
                 * @returns {Number} sungle number of months represented by entire source
                 */
                getMonths() {
                    let months = 0;

                    switch(this.aggregation_method){
                        case 'max':
                            months = this.data.map(entry => this.durationToMonths(this.durationGetter(entry)));
                            return months.reduce((max, months) => Math.max(max, months), 0);
                        case 'sum':
                            months = this.data.map(entry => this.durationToMonths(this.durationGetter(entry)));
                            return months.reduce((sum, months) => sum + months, 0);
                        case 'none':
                        default:
                            return this.durationToMonths(this.durationGetter(this.data));
                    }
                }

                /**
                 * notifies Source and AutoDurationMandationService of source data changes.
                 * this has to be called when the underlying array os changes (not modified but a new array is created)
                 * @param {Object|Array|null} data optional, replaces the existing data
                 */
                notifyChange(data=null){
                    this.data = data || this.data;
                    this.auto_duration_mandation_service.update();
                }

                get(){
                    let has_generated_entry = Boolean(this.auto_duration_mandation_service.generated_entry);
                    if(this.append_generated_entry && has_generated_entry){
                        return [...this.data, this.auto_duration_mandation_service.generated_entry];
                    }else{
                        return [...this.data];
                    }
                }

                set(entries){
                    let has_generated_entry = Boolean(this.auto_duration_mandation_service.generated_entry);
                    if(this.append_generated_entry && has_generated_entry){
                        this.data = entries.slice(0, -1);
                    }else{
                        this.data = entries;
                    }
                    return this.data;
                }

            }

            this.target_months = target_months;
            this.sources = [];
            this.generated_entry = null;

        }

        /**
         * registeres new sources that are considered when computing the total number of months
         * @param {this.Source} source Source instance that links to the data and holds other parameters
         * @param {Boolean} generative whenther this source is to be used to generate new entries
         */
        registerSource(source) {
            this.sources.push(source);
        }

        createTemporaryEntry() {
            return null;
        }

        makeTemporaryEntryPermanent(entry) {
            
        }

        /**
         * combines all sources durations in months into one total numbe rof months
         * @return {Number} total number of months represented by the entire dataset associated with this sercies
         */
        getTotalDuration() {
            return this.sources.reduce((sum, source) => sum + source.getMonths(), 0)
        }

        /**
         * compute total duration and decide if an entry needs to be generated or removed
         * @param {any} entry 
         */
        update(entry=null) {

            // if entry is give, it indicated it has been changed, if it was the generated entry,
            // it can no longer be marked as generated
            if(this.generated_entry && entry && this.generated_entry == entry){
                this.generated_entry = null;
                this.makeTemporaryEntryPermanent(entry);
            }

            // get total number of months represented by all sources
            let total_months = this.getTotalDuration();

            // if total months does not meet target, and there is no current generated entry, 
            // request to generate a new entry
            if(total_months < this.target_months){
                if(this.generated_entry == null){
                    this.generated_entry = this.createTemporaryEntry();
                }

            // if the target is met and a generated entry exists, 
            // request for the generated entry to be removed
            // NOTE that if a generated entry has been edited, it us unmarked 
            // and this should never delete entered information
            }else{
                if(this.generated_entry){
                    this.generated_entry = null;
                }
            }
        }

    },
    TypePauseDetection: class {

        constructor(callback, timeout=300) {
            this.callback = callback;
            this.timeout = timeout;
            this.touch_count = 0;
        }

        touch(...args) {
            let self = this;
            let count = ++self.touch_count;
            window.setTimeout(function(){
                if(count == self.touch_count){
                    self.callback(...args);
                }
            }, this.timeout);
        }

    },

    monthlyFrequencyConversionFactor,
    frequencyConversionFactor,
    calculateRepayments,
    checkBussinessQueryType
};